/**
 * Compact AI capacity meters from ledger snapshots + outage signals.
 *
 * Empty / unknown states stay honest: no fake percentages. A pool with no
 * reading appears as unknown rather than 0% used.
 */

import type { UsageLedgerSnapshot, UsageQuotaSignal } from './contract.js';
import {
  evaluateUsageLimits,
  usedPercentOf,
  type AiUsagePolicy,
  type UsageLimitBand,
  DEFAULT_AI_USAGE_POLICY,
} from './limits.js';
import { measurementBadge, formatUsageFigure, tagMeasurement } from './measurement.js';

export type MeterTone = 'success' | 'attention' | 'danger' | 'neutral';

export interface AiCapacityMeter {
  readonly key: string;
  readonly title: string;
  readonly provider: string;
  readonly runtime: string | null;
  readonly model: string | null;
  readonly nodeId: string | null;
  readonly poolKey: string | null;
  /** Used percent when known; null when unknown — never a guessed 0. */
  readonly usedPercent: number | null;
  readonly remainingLabel: string | null;
  readonly usedLabel: string | null;
  readonly resetsLabel: string | null;
  readonly measurementLabel: string | null;
  readonly measurementPhrase: string;
  readonly quotaSignal: UsageQuotaSignal;
  readonly band: UsageLimitBand;
  readonly tone: MeterTone;
  readonly hosting: string;
  readonly billingKind: string;
  readonly detailLine: string;
  readonly known: boolean;
}

export interface OutageSignalLike {
  readonly runtime_key: string;
  readonly reason_code: string;
  readonly available_from: string | null;
  readonly name?: string | null;
}

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

function titleFor(snapshot: UsageLedgerSnapshot): string {
  if (snapshot.label && snapshot.label.trim() !== '') return snapshot.label.trim();
  const parts = [snapshot.provider, snapshot.runtime, snapshot.model].filter(
    (part): part is string => part !== null && part.trim() !== '',
  );
  return parts.length > 0 ? parts.join(' · ') : snapshot.provider;
}

function resetsLabel(resetsAt: string | null, nowMs: number): string | null {
  if (resetsAt === null) return null;
  const at = Date.parse(resetsAt);
  if (Number.isNaN(at)) return null;
  const delta = at - nowMs;
  if (delta <= 0) return 'reset due';
  if (delta < HOUR_MS) return `resets in ${Math.max(1, Math.floor(delta / MINUTE_MS))}m`;
  if (delta < DAY_MS) return `resets in ${Math.floor(delta / HOUR_MS)}h`;
  return `resets in ${Math.floor(delta / DAY_MS)}d`;
}

function toneFor(band: UsageLimitBand, signal: UsageQuotaSignal): MeterTone {
  if (band === 'exhausted' || signal === 'exhausted' || signal === 'signed_out') return 'danger';
  if (band === 'hard_exceeded' || band === 'reserve_breached') return 'danger';
  if (band === 'soft_exceeded' || band === 'warn' || signal === 'near_limit' || signal === 'rate_limited') {
    return 'attention';
  }
  if (band === 'unknown' || signal === 'unknown') return 'neutral';
  return 'success';
}

function detailLine(meter: {
  readonly known: boolean;
  readonly usedPercent: number | null;
  readonly remainingLabel: string | null;
  readonly usedLabel: string | null;
  readonly resetsLabel: string | null;
  readonly measurementLabel: string | null;
  readonly quotaSignal: UsageQuotaSignal;
  readonly runtime: string | null;
  readonly model: string | null;
}): string {
  if (!meter.known) return 'No measured reading yet.';
  const bits: string[] = [];
  if (meter.usedPercent !== null) bits.push(`${Math.round(meter.usedPercent)}% used`);
  else if (meter.usedLabel !== null) bits.push(`${meter.usedLabel} used`);
  if (meter.remainingLabel !== null) bits.push(`${meter.remainingLabel} left`);
  if (meter.resetsLabel !== null) bits.push(meter.resetsLabel);
  if (meter.runtime !== null) bits.push(meter.runtime);
  if (meter.model !== null) bits.push(meter.model);
  if (meter.measurementLabel !== null) bits.push(meter.measurementLabel);
  if (meter.quotaSignal === 'exhausted') bits.push('exhausted');
  if (meter.quotaSignal === 'rate_limited') bits.push('rate limited');
  if (meter.quotaSignal === 'signed_out') bits.push('signed out');
  return bits.length > 0 ? bits.join(' · ') : 'Reading on file; no usable percent.';
}

function signalFromOutage(reason: string): UsageQuotaSignal {
  if (reason === 'out_of_usage') return 'exhausted';
  if (reason === 'signed_out') return 'signed_out';
  return 'unknown';
}

/**
 * Build one meter from a ledger snapshot. Outages for the same runtime upgrade
 * the quota signal when the ledger has not already marked exhaustion.
 */
export function meterFromSnapshot(
  snapshot: UsageLedgerSnapshot,
  policy: AiUsagePolicy = DEFAULT_AI_USAGE_POLICY,
  nowMs: number,
  outages: readonly OutageSignalLike[] = [],
): AiCapacityMeter {
  // A bare non-percent count (for example 12 credits) is not 12%.
  // Only percent readings may stand alone; other units need an explicit total allowance.
  const usedPercent = snapshot.measureUnit === 'percent' || snapshot.limitValue !== null
    ? usedPercentOf(snapshot.usedValue, snapshot.remainingValue, snapshot.limitValue)
    : null;
  const exhausted = snapshot.quotaSignal === 'exhausted'
    || outages.some((row) => row.runtime_key === snapshot.runtime && row.reason_code === 'out_of_usage');
  const signedOut = snapshot.quotaSignal === 'signed_out'
    || outages.some((row) => row.runtime_key === snapshot.runtime && row.reason_code === 'signed_out');

  let quotaSignal: UsageQuotaSignal = snapshot.quotaSignal;
  if (exhausted) quotaSignal = 'exhausted';
  else if (signedOut) quotaSignal = 'signed_out';
  else if (usedPercent !== null && usedPercent >= 90 && quotaSignal === 'ok') quotaSignal = 'near_limit';

  const evaluation = evaluateUsageLimits(usedPercent, policy, exhausted || quotaSignal === 'exhausted');
  const tag = tagMeasurement(snapshot.readingOrigin, snapshot.confidence);
  const hasFigure = snapshot.usedValue !== null
    || snapshot.remainingValue !== null
    || snapshot.limitValue !== null
    || snapshot.resetsAt !== null;
  const known = hasFigure || exhausted || signedOut;

  const meterBase = {
    known,
    usedPercent: evaluation.usedPercent,
    remainingLabel: formatUsageFigure(snapshot.remainingValue, snapshot.measureUnit),
    usedLabel: formatUsageFigure(snapshot.usedValue, snapshot.measureUnit),
    resetsLabel: resetsLabel(snapshot.resetsAt, nowMs),
    measurementLabel: measurementBadge(hasFigure, snapshot.readingOrigin, snapshot.confidence),
    quotaSignal,
    runtime: snapshot.runtime,
    model: snapshot.model,
  };

  return {
    key: [
      snapshot.provider,
      snapshot.accountRef ?? '',
      snapshot.poolKey ?? '',
      snapshot.runtime ?? '',
    ].join(':'),
    title: titleFor(snapshot),
    provider: snapshot.provider,
    runtime: snapshot.runtime,
    model: snapshot.model,
    nodeId: snapshot.nodeId,
    poolKey: snapshot.poolKey,
    usedPercent: evaluation.usedPercent,
    remainingLabel: meterBase.remainingLabel,
    usedLabel: meterBase.usedLabel,
    resetsLabel: meterBase.resetsLabel,
    measurementLabel: meterBase.measurementLabel,
    measurementPhrase: tag.phrase,
    quotaSignal,
    band: evaluation.band,
    tone: toneFor(evaluation.band, quotaSignal),
    hosting: snapshot.hosting,
    billingKind: snapshot.billingKind,
    detailLine: detailLine(meterBase),
    known,
  };
}

/**
 * Meters for the cockpit strip. Outage-only runtimes (no measurement yet) get
 * an honest exhausted/signed-out meter with no invented percent.
 */
export function buildAiCapacityMeters(
  snapshots: readonly UsageLedgerSnapshot[],
  outages: readonly OutageSignalLike[],
  nowMs: number,
  policy: AiUsagePolicy = DEFAULT_AI_USAGE_POLICY,
): readonly AiCapacityMeter[] {
  const meters = snapshots.map((snapshot) => meterFromSnapshot(snapshot, policy, nowMs, outages));
  const coveredRuntimes = new Set(
    meters.map((meter) => meter.runtime).filter((runtime): runtime is string => runtime !== null),
  );

  for (const outage of outages) {
    if (coveredRuntimes.has(outage.runtime_key)) continue;
    coveredRuntimes.add(outage.runtime_key);
    const signal = signalFromOutage(outage.reason_code);
    const evaluation = evaluateUsageLimits(null, policy, signal === 'exhausted');
    meters.push({
      key: `outage:${outage.runtime_key}:${outage.name ?? ''}`,
      title: outage.runtime_key,
      provider: outage.runtime_key,
      runtime: outage.runtime_key,
      model: null,
      nodeId: null,
      poolKey: null,
      usedPercent: null,
      remainingLabel: null,
      usedLabel: null,
      resetsLabel: resetsLabel(outage.available_from, nowMs),
      measurementLabel: null,
      measurementPhrase: 'observed exhaustion or sign-out — no measured allowance figure',
      quotaSignal: signal,
      band: evaluation.band,
      tone: toneFor(evaluation.band, signal),
      hosting: 'unknown',
      billingKind: 'unknown',
      detailLine: signal === 'exhausted'
        ? 'Observed out of usage — no measured percent on file.'
        : signal === 'signed_out'
          ? 'Signed out — no measured percent on file.'
          : 'Outage reported — no measured percent on file.',
      known: true,
    });
  }

  return meters.sort((left, right) => left.title.localeCompare(right.title));
}

/** Guidance line under the meters strip. */
export const AI_USAGE_GUIDANCE =
  'Consult Overseer to set limits, routing preferences, and usage policies.';
